/* eslint-disable no-unused-vars */
import { Button } from "../../ui/Button.jsx";
import { motion, AnimatePresence } from "motion/react";
import { useState } from "react";
import { brandingConfig } from "../../../content/config/branding.js";
import { iconMap } from "../../ui/Icons.jsx"; // importar el mapa de iconos

// Función para determinar estilos de badge (igual que en MediaFeature.jsx)
const getBadgeStyles = (b) => {
  const t = String(b || "")
    .toLowerCase()
    .trim();
  const { colors } = brandingConfig;

  if (t === "nuevo")
    return { backgroundColor: colors.accent2, color: colors.white };
  if (t === "tendencia" || t === "intenso")
    return { backgroundColor: colors.accent, color: colors.white };
  if (t === "popular") return { backgroundColor: "#3B82F6", color: "#FFFFFF" };
  return { backgroundColor: "#1F2937", color: colors.white };
};

export const MobileMenu = ({
  isOpen,
  onClose,
  locationButton,
  navigation,
  ctaButton,
  experienciasDropdown,
  dropdownStates,
  toggleDropdown,
}) => {
  const [currentPage, setCurrentPage] = useState("main"); // 'main' | 'experiencias'

  // Función auxiliar: renderiza iconos por nombre usando el iconMap
  // Acepta nombres definidos en src/components/ui/Icons.jsx (por ejemplo "ExternalIcon", "LocationIcon", etc.)
  const renderIcon = (iconName) => {
    if (!iconName) return null;
    const Icon = iconMap?.[iconName];
    if (!Icon) return null;
    return <Icon className="w-4 h-4" />;
  };

  // Resetear página al cerrar
  const handleClose = () => {
    setCurrentPage("main");
    onClose();
  };

  // Animaciones del contenedor principal
  const menuVariants = {
    closed: {
      x: "-100%",
      transition: {
        type: "tween",
        duration: 0.3,
        ease: "easeInOut",
      },
    },
    open: {
      x: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        mass: 0.8,
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  // Animaciones para elementos individuales
  const itemVariants = {
    closed: {
      x: -50,
      opacity: 0,
    },
    open: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 25,
        mass: 0.5,
      },
    },
  };

  // Animaciones para transición entre páginas
  const pageVariants = {
    enter: {
      x: "100%",
      opacity: 0,
    },
    center: {
      x: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.08,
        delayChildren: 0.1,
      },
    },
    exit: {
      x: "-100%",
      opacity: 0,
      transition: {
        type: "tween",
        duration: 0.2,
        ease: "easeInOut",
      },
    },
  };

  if (!isOpen) return null;

  return (
    <motion.div
      className="lg:hidden fixed inset-0 z-50"
      initial="closed"
      animate="open"
      exit="closed"
      variants={menuVariants}
    >
      {/* Fondo del menú con gradiente */}
      <div className="flex flex-col h-full bg-gradient-to-b from-primary via-primary to-primary/80">
        {/* Mobile Header */}
        <motion.div
          className="flex items-center justify-between p-4 border-b border-white/20"
          variants={itemVariants}
        >
          <div className="flex-1">
            {/* Botón de regresar - Solo visible en página de experiencias */}
            <AnimatePresence>
              {currentPage === "experiencias" && (
                <motion.button
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  onClick={() => setCurrentPage("main")}
                  className="flex items-center text-white hover:text-accent-400 transition-colors duration-200"
                >
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                  <span className="font-sweet text-sm font-medium">
                    Regresar
                  </span>
                </motion.button>
              )}
            </AnimatePresence>
          </div>

          <motion.button
            onClick={handleClose}
            className="text-white p-2 hover:bg-white/10 rounded-lg transition-colors duration-200"
            aria-label="Cerrar menú móvil"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </motion.button>
        </motion.div>

        {/* Mobile Content con AnimatePresence para transiciones */}
        <div className="flex-1 overflow-hidden relative">
          <AnimatePresence mode="wait">
            {currentPage === "main" ? (
              <motion.div
                key="main-page"
                className="absolute inset-0 overflow-y-auto"
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                {/* Location Button - Móvil */}
                {locationButton.show && (
                  <motion.div
                    className="p-4 border-b border-white/10"
                    variants={itemVariants}
                  >
                    <Button
                      color="white"
                      size="small"
                      onClick={() => {
                        locationButton.onClick?.();
                        handleClose();
                      }}
                      icon={"LocationIcon"}
                      iconPosition="left"
                      className="w-full justify-start text-sm"
                    >
                      {locationButton.text}
                    </Button>
                  </motion.div>
                )}

                {/* Mobile Navigation */}
                <motion.nav className="p-4" variants={itemVariants}>
                  <div className="space-y-2">
                    {/* Experiencias - Botón para ir a la segunda página */}
                    {experienciasDropdown.show && (
                      <motion.div variants={itemVariants}>
                        <motion.button
                          className="block text-white hover:text-accent-400 p-3 rounded-lg hover:bg-white/10 w-full text-left font-sweet text-sm font-medium transition-all duration-200"
                          onClick={() => setCurrentPage("experiencias")}
                          whileHover={{ x: 5 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <div className="flex items-center justify-between">
                            EXPERIENCIAS
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </div>
                        </motion.button>
                      </motion.div>
                    )}

                    {/* Resto de navegación */}
                    {navigation
                      .filter((item) => item.label !== "EXPERIENCIAS")
                      .map((item, index) => (
                        <motion.div key={index} variants={itemVariants}>
                          {item.href ? (
                            <motion.a
                              href={item.href}
                              target={item.external ? "_blank" : "_self"}
                              rel={item.external ? "noopener noreferrer" : ""}
                              className="block text-white hover:text-accent-400 p-3 rounded-lg hover:bg-white/10 font-sweet text-sm font-medium transition-all duration-200"
                              onClick={handleClose}
                              whileHover={{ x: 5 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              {/* Contenido con icono a la derecha si existe item.icon */}
                              <div className="flex items-center justify-between">
                                <span>{item.label}</span>
                                {item.icon && (
                                  <span className="ml-2 flex-shrink-0">
                                    {renderIcon(item.icon)}
                                  </span>
                                )}
                              </div>
                            </motion.a>
                          ) : (
                            <>
                              <motion.button
                                className="block text-white hover:text-accent-400 p-3 rounded-lg hover:bg-white/10 w-full text-left font-sweet text-sm font-medium transition-all duration-200"
                                onClick={() => toggleDropdown("general", index)}
                                whileHover={{ x: 5 }}
                                whileTap={{ scale: 0.98 }}
                              >
                                <div className="flex items-center justify-between">
                                  {item.label}
                                  {item.dropdown && (
                                    <motion.svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                      animate={{
                                        rotate:
                                          dropdownStates.general === index
                                            ? 180
                                            : 0,
                                      }}
                                      transition={{ duration: 0.2 }}
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 9l-7 7-7-7"
                                      />
                                    </motion.svg>
                                  )}
                                </div>
                              </motion.button>

                              {/* Mobile Dropdown */}
                              <AnimatePresence>
                                {item.dropdown &&
                                  dropdownStates.general === index && (
                                    <motion.div
                                      className="ml-4 mt-2 space-y-1 overflow-hidden"
                                      initial={{ height: 0, opacity: 0 }}
                                      animate={{ height: "auto", opacity: 1 }}
                                      exit={{ height: 0, opacity: 0 }}
                                      transition={{
                                        duration: 0.3,
                                        ease: "easeInOut",
                                      }}
                                    >
                                      {item.dropdown.map(
                                        (dropdownItem, dropdownIndex) => (
                                          <motion.a
                                            key={dropdownIndex}
                                            href={dropdownItem.href}
                                            target={
                                              dropdownItem.external
                                                ? "_blank"
                                                : "_self"
                                            }
                                            rel={
                                              dropdownItem.external
                                                ? "noopener noreferrer"
                                                : ""
                                            }
                                            className="block text-gray-300 hover:text-white p-2 rounded-lg hover:bg-white/10 font-sweet text-sm transition-all duration-200"
                                            onClick={handleClose}
                                            initial={{ x: -20, opacity: 0 }}
                                            animate={{ x: 0, opacity: 1 }}
                                            transition={{
                                              delay: dropdownIndex * 0.05,
                                            }}
                                            whileHover={{ x: 5 }}
                                          >
                                            {dropdownItem.label}
                                          </motion.a>
                                        )
                                      )}
                                    </motion.div>
                                  )}
                              </AnimatePresence>
                            </>
                          )}
                        </motion.div>
                      ))}
                  </div>
                </motion.nav>
              </motion.div>
            ) : (
              // Página de Experiencias
              <motion.div
                key="experiencias-page"
                className="absolute inset-0 overflow-y-auto"
                variants={pageVariants}
                initial="enter"
                animate="center"
                exit="exit"
              >
                <motion.div className="p-4">
                  <motion.h2
                    className="text-white font-sweet text-lg font-bold mb-4"
                    variants={itemVariants}
                  >
                    Experiencias
                  </motion.h2>

                  <motion.div className="space-y-2" variants={itemVariants}>
                    {experienciasDropdown.items.map((item, itemIndex) => (
                      <motion.a
                        key={itemIndex}
                        href={item.href}
                        className="block text-white hover:text-accent-400 p-3 rounded-lg hover:bg-white/10 font-sweet text-sm font-medium transition-all duration-200"
                        onClick={handleClose}
                        variants={itemVariants}
                        whileHover={{ x: 5, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <div className="flex items-center justify-between">
                          <span>{item.label}</span>
                          {item.badge && (
                            <motion.span
                              className="text-xs px-2 py-1 rounded-full font-bold uppercase"
                              style={getBadgeStyles(item.badge.text)}
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              transition={{
                                delay: itemIndex * 0.1 + 0.3,
                                type: "spring",
                              }}
                            >
                              {item.badge.text}
                            </motion.span>
                          )}
                        </div>
                      </motion.a>
                    ))}
                  </motion.div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Área transparente inferior con botón de reserva */}
        <motion.div
          className="bg-gradient-to-b from-transparent via-primary/60 to-primary/80 backdrop-blur-sm"
          variants={itemVariants}
        >
          {ctaButton.show && (
            <div className="p-6">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  background="secondary"
                  color="white"
                  size="medium"
                  href={ctaButton.href}
                  className="w-full justify-center text-base font-bold shadow-lg hover:shadow-xl transition-all duration-300"
                  onClick={handleClose}
                  animation="ripple"
                >
                  {ctaButton.text}
                </Button>
              </motion.div>
            </div>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};
